import { Check, Clock3, FileUp, LucideIcon, Upload } from 'lucide-react';
import { Inline } from '../../../../../../design-system/inline/inline';
import { Stack } from '../../../../../../design-system/stack/stack';
import { exhaustiveCheck } from '../../../../../../utils/exhaustive-check';

export type XeroFeature = 'SYNC_TRANSACTIONS' | 'SEND_INVOICES' | 'RECONCILIATION';
type XeroFeatureStatus = 'ACTIVE' | 'INACTIVE';

type XeroFeatureLineItemProps = {
  feature: XeroFeature;
};

export const XeroFeatureLineItem = ({ feature }: XeroFeatureLineItemProps) => {
  const { icon: Icon, title, description, status } = getLineItemDetails(feature);

  return (
    <Inline justify="between" wrap="nowrap">
      <Inline space={3} wrap="nowrap">
        <div className="h-8 w-8 rounded-full bg-white p-2">
          <Icon strokeWidth={1} color="black" className="h-full w-full" />
        </div>

        <Stack space={0}>
          <h3 className="text-sm leading-6 font-normal tracking-tight text-black">{title}</h3>

          <p className="text-xs leading-6 font-normal tracking-tight text-muted-foreground">{description}</p>
        </Stack>
      </Inline>

      {status === 'ACTIVE' ? (
        <div className="h-5 w-5">
          <Check color="#169B62" strokeWidth={1} aria-label="Feature is available" className="h-full w-full" />
        </div>
      ) : (
        <p
          aria-label="Feature is coming soon"
          className="text-xs leading-5 font-normal tracking-tight text-nowrap text-black"
        >
          Coming Soon
        </p>
      )}
    </Inline>
  );
};

const getLineItemDetails = (
  feature: XeroFeature,
): {
  icon: LucideIcon;
  title: string;
  description: string;
  status: XeroFeatureStatus;
} => {
  switch (feature) {
    case 'SYNC_TRANSACTIONS':
      return {
        icon: FileUp,
        title: 'Sync all your Seapoint transactions to Xero',
        description: 'Keep your ledger accurate and always up to date',
        status: 'ACTIVE',
      };
    case 'SEND_INVOICES':
      return {
        icon: Upload,
        title: 'Send invoices and mark them as paid automatically',
        description: `Upload and pay in Seapoint and we'll handle the rest`,
        status: 'ACTIVE',
      };
    case 'RECONCILIATION':
      return {
        icon: Clock3,
        title: 'Streamline reconciliation',
        description: 'Map your Seapoint categories to Xero to simplify your bookkeeping',
        status: 'INACTIVE',
      };
    default:
      return exhaustiveCheck(feature);
  }
};
